import {
  AutocompleteInputChangeReason,
  AutocompleteRenderGroupParams,
  createFilterOptions,
  SxProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { WorkLocation } from "../../../../../features/customer/types/queries";
import { Nullable } from "../../../../types/nullable";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import AutocompleteBase from "../../fields/autocompleteBase/AutocompleteBase";
import { AutocompleteBaseSingle } from "../../fields/autocompleteBase/AutocompleteBaseSingle";

type SharedProps = {
  options: WorkLocation[];
  onInputChange: (value: string, reason: AutocompleteInputChangeReason) => void;
  isLoading?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  limitTags?: number;
  required?: boolean;
  error?: boolean;
  name?: string;
  getOptionDisabled?: (option: WorkLocation) => boolean;
  disableClearable?: boolean;
};

type MultiProps = SharedProps & {
  multiple: true;
  value: WorkLocation[];
  onChange: (value: WorkLocation[] | null) => void;
};

type SingleProps = SharedProps & {
  multiple?: false;
  value: Nullable<WorkLocation>;
  onChange: (value: Nullable<WorkLocation>) => void;
};

type Props = MultiProps | SingleProps;

const workLocationFilterOptions = createFilterOptions<WorkLocation>({
  stringify: (option) => JSON.stringify(option),
});

const sharedAutocompleteProps = {
  getOptionLabel: (option: WorkLocation) => option.name,
  isOptionEqualToValue: (option: WorkLocation, value: WorkLocation) =>
    option.id === value.id,
  filterOptions: workLocationFilterOptions,
  groupBy: (option: WorkLocation) => option.customerOrgName || "",
  renderGroup: ({ group, children }: AutocompleteRenderGroupParams) => (
    <AutocompleteGroupedList group={group} children={children} />
  ),
};

const WorkLocationAutocomplete = ({
  options,
  value,
  onInputChange,
  onChange,
  isLoading = false,
  disabled,
  sx,
  getOptionDisabled,
  disableClearable,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const mergedSx = { backgroundColor: "rgba(249, 249, 250, 0.6)", ...sx };

  if (rest.multiple) {
    return (
      <AutocompleteBase<WorkLocation>
        multiple
        options={options}
        value={value as WorkLocation[]}
        loading={isLoading}
        {...sharedAutocompleteProps}
        onInputChange={(_, val, reason) => onInputChange(val, reason)}
        onChange={onChange as (value: WorkLocation[] | null) => void}
        limitTags={rest.limitTags}
        disabled={disabled}
        noOptionsText={t("common:writeToSearch")}
        error={rest.error}
        required={rest.required}
        getOptionDisabled={getOptionDisabled}
        disableClearable={disableClearable}
        sx={mergedSx}
      />
    );
  }

  return (
    <AutocompleteBaseSingle
      options={options}
      value={value as Nullable<WorkLocation>}
      loading={isLoading}
      {...sharedAutocompleteProps}
      onInputChange={(_, val, reason) => onInputChange(val, reason)}
      onChange={onChange as (value: Nullable<WorkLocation>) => void}
      disabled={disabled}
      noOptionsText={t("common:writeToSearch")}
      error={rest.error}
      required={rest.required}
      name={rest.name}
      sx={mergedSx}
    />
  );
};

export default WorkLocationAutocomplete;
